import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Table2 } from "lucide-react";
import GenericFilterBar from "@/components/GenericFilterBar";
import {
  contextFromRows, contextFromWideRows, contextFromGrouped,
} from "@/analytics/charts/chart.context";
import SimpleChartGrid, { ChartItem } from "@/components/SimpleChartGrid";
import { NoAccess } from "@/components/NoAccess";
import { isForbiddenError } from "@/services/apiClient";
import { ChatDock } from "@/components/ChatDock";
import { MobileTopBar } from "@/components/MobileTopBar";
import UtilisationSkeleton from "@/components/UtilisationSkeleton";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer,
} from "recharts";
import {
  CORE_PALETTE, getChartColors, AXIS_TICK_STYLE, CHART_MARGIN, formatYAxisLabel, renderCategoryTick, renderMonthYearTick, MONTH_YEAR_AXIS_HEIGHT,
} from "@/analytics/chart.utilities";
import { getDefaultDateRange } from "@/analytics/api";
import {
  fetchRecoveryFilterOptions,
  fetchRecoveryDateRange,
  fetchRecoveryChargedMonthly,
  fetchRecoveryBilledNetMonthly,
  fetchRecoveryWriteOffMonthly,
  fetchRecoveryPctMonthly,
  fetchRecoveryPctByServiceLine,
  fetchRecoveryPctByLocation,
  fetchRecoveryTopManagersWriteOff,
  fetchRecoveryTopAssignments,
  type RecoveryFilters,
  type RecoveryFilterOptions,
} from "@/analytics/recovery.api";
import { makeFilterCache } from "@/lib/filterPersistence";
import { useChatVisibility } from "@/hooks/useChatVisibility";

const EMPTY_FILTERS: RecoveryFilters = {
  managers: [], client_partners: [], assignment_names: [], service_lines: [],
  legacy_location: [], date_from: null, date_to: null,
};

// Cached max_month from previous session lets us fire chart fetches in parallel
// with filter-options/date-range, skipping the sequential gate.
function isSingleMonth(date_from: string | null, date_to: string | null): boolean {
  if (!date_from || !date_to) return false;
  const [fy, fm] = date_from.split("T")[0].split("-");
  const [ty, tm] = date_to.split("T")[0].split("-");
  return fy === ty && fm === tm;
}

const MAX_MONTH_CACHE_KEY = "prism:recovery:max_month";
const FILTERS_STORAGE_KEY = "prism:recovery:filters";
const readCachedMaxMonth = (): string | null => {
  try { return localStorage.getItem(MAX_MONTH_CACHE_KEY); } catch { return null; }
};
const writeCachedMaxMonth = (v: string | null) => {
  try { v ? localStorage.setItem(MAX_MONTH_CACHE_KEY, v) : localStorage.removeItem(MAX_MONTH_CACHE_KEY); } catch { /* no-op */ }
};

const filterCache = makeFilterCache<RecoveryFilters>("prism:recovery:filters");

const CHART_KEYS = [
  "charged", "billedNet", "writeOff", "pct", "pctBySL", "pctByLoc", "topMgrs", "topAssign",
] as const;

const RecoveryPage = () => {
  const navigate = useNavigate();
  const savedFiltersRef = useRef<RecoveryFilters | null>(readSavedRecoveryFilters());
  const [opts, setOpts] = useState<RecoveryFilterOptions>({
    managers: [], client_partners: [], assignment_names: [], service_lines: [], legacy_location: [],
    min_month: null, max_month: null,
  });
  const [filters, setFilters] = useState<RecoveryFilters>(() => {
    const persisted = filterCache.read();
    if (persisted) return persisted;
    const cached = readCachedMaxMonth();
    if (!cached) return EMPTY_FILTERS;
    const { date_from, date_to } = getDefaultDateRange(null, cached);
    return { ...EMPTY_FILTERS, date_from, date_to };
  });
  // ready=true immediately if we have a cached max_month — lets chart fetches run
  // in parallel with the options/date-range round trips.
  const [ready, setReady] = useState(() => readCachedMaxMonth() !== null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [noAccess, setNoAccess] = useState(false);
  const [data, setData] = useState<any>({});
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [loadingKeys, setLoadingKeys] = useState<Set<string>>(new Set());
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [validatedFilterKey, setValidatedFilterKey] = useState<string | null>(null);
  const [isAssistantOpen, setIsAssistantOpen] = useChatVisibility();
  const abortRef = useRef<AbortController | null>(null);
  const optionsAbortRef = useRef<AbortController | null>(null);
  const hasLoadedOnce = useRef(false);
  const palette = getChartColors().solid;

  useEffect(() => { filterCache.write(filters); }, [filters]);

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      const saved = localStorage.getItem("prism:sidebar:open");
      return saved !== null ? saved === "true" : true;
    } catch { return true; }
  });
  const handleSidebarChange = (open: boolean) => {
    setSidebarOpen(open);
    try { localStorage.setItem("prism:sidebar:open", String(open)); } catch {}
  };

  useEffect(() => {
    Promise.all([fetchRecoveryFilterOptions(EMPTY_FILTERS), fetchRecoveryDateRange()])
      .then(([o, dr]) => {
        writeCachedMaxMonth(dr.max_month);
        const { date_from, date_to } = getDefaultDateRange(dr.min_month, dr.max_month);
        setOpts({ ...o, min_month: dr.min_month, max_month: dr.max_month });
        setFilters(prev => {
          if (!prev.date_from && !prev.date_to) return { ...prev, date_from, date_to };
          return prev.date_to === date_to ? prev : { ...prev, date_to };
        });
        setReady(true);
      })
      .catch(err => {
        // A total first-load failure used to only log, leaving every card
        // blank with no retry — which reads as "no data" rather than
        // "failed". Mark them all errored so each card offers Retry.
        console.error(err);
        setErrors(Object.fromEntries(CHART_KEYS.map(k => [k, true])));
        setLoadingKeys(new Set());
        setReady(true);
      });
  }, []);

  useEffect(() => {
    const defaults = getDefaultDateRange(opts.min_month, opts.max_month);
    const isDefault = !filters.managers.length && !filters.client_partners.length && !filters.assignment_names.length && !filters.service_lines.length && !filters.legacy_location.length && filters.date_from === defaults.date_from && filters.date_to === defaults.date_to;
    try { isDefault ? localStorage.removeItem(FILTERS_STORAGE_KEY) : localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters)); } catch { /* no-op */ }
  }, [filters, opts.min_month, opts.max_month]);

  useEffect(() => {
    if (!ready) return;
    optionsAbortRef.current?.abort();
    const controller = new AbortController();
    optionsAbortRef.current = controller;
    const requestKey = JSON.stringify(filters);
    setOptionsLoading(true);
    setValidatedFilterKey(null);
    const timeout = window.setTimeout(() => {
      fetchRecoveryFilterOptions(filters, controller.signal)
        .then(nextOptions => {
          if (controller.signal.aborted) return;
          setOpts(prev => ({ ...nextOptions, min_month: prev.min_month, max_month: prev.max_month }));
          setValidatedFilterKey(requestKey);
        })
        .catch(err => { if (!controller.signal.aborted && err?.name !== "AbortError") { console.error(err); setValidatedFilterKey(requestKey); } })
        .finally(() => { if (!controller.signal.aborted) setOptionsLoading(false); });
    }, 250);
    return () => { window.clearTimeout(timeout); controller.abort(); };
  }, [filters, ready]);

  // Per-chart fetcher map keeps retry + non-destructive updates DRY.
  const fetcherMap = (f: RecoveryFilters, signal?: AbortSignal) => ({
    charged:   () => fetchRecoveryChargedMonthly(f, signal).then(d => ({ charged: d.data })),
    billedNet: () => fetchRecoveryBilledNetMonthly(f, signal).then(d => ({ billedNet: d.data })),
    writeOff:  () => fetchRecoveryWriteOffMonthly(f, signal).then(d => ({ writeOff: d.data })),
    pct:       () => fetchRecoveryPctMonthly(f, signal).then(d => ({ pct: d.data })),
    pctBySL:   () => fetchRecoveryPctByServiceLine(f, signal).then(d => ({ pctBySL: d })),
    pctByLoc:  () => fetchRecoveryPctByLocation(f, signal).then(d => ({ pctByLoc: d })),
    topMgrs:   () => fetchRecoveryTopManagersWriteOff(f, 10, signal).then(d => ({ topMgrs: d.data })),
    topAssign: () => fetchRecoveryTopAssignments(f, 10, "billed_net", signal).then(d => ({ topAssign: d.data })),
  });

  useEffect(() => {
    if (!ready || validatedFilterKey !== JSON.stringify(filters)) return;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const { signal } = ctrl;
    setNoAccess(false);

    const isFirst = !hasLoadedOnce.current;
    const fetchers = fetcherMap(filters, signal);

    if (isFirst) {
      setInitialLoading(true);
      Promise.all(CHART_KEYS.map(k => fetchers[k]()))
        .then(results => {
          if (signal.aborted) return;
          const merged = results.reduce((acc, r) => ({ ...acc, ...r }), {} as any);
          setData(merged);
          hasLoadedOnce.current = true;
        })
        .catch(err => {
          if (signal.aborted || err?.name === "AbortError") return;
          if (isForbiddenError(err)) setNoAccess(true);
          else console.error(err);
        })
        .finally(() => {
          if (!signal.aborted) setInitialLoading(false);
        });
      return;
    }

    setLoadingKeys(new Set(CHART_KEYS));
    setErrors({});

    CHART_KEYS.forEach(k => {
      fetchers[k]()
        .then(r => {
          if (signal.aborted) return;
          setData((s: any) => ({ ...s, ...r }));
          setLoadingKeys(prev => { const n = new Set(prev); n.delete(k); return n; });
        })
        .catch(err => {
          if (signal.aborted || err?.name === "AbortError") return;
          if (isForbiddenError(err)) { setNoAccess(true); return; }
          setErrors(prev => ({ ...prev, [k]: true }));
          setLoadingKeys(prev => { const n = new Set(prev); n.delete(k); return n; });
        });
    });
  }, [filters, ready, validatedFilterKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Per-chart retry — fetches only the failed source.
  const retryChart = (key: string) => {
    setErrors(prev => { const n = { ...prev }; delete n[key]; return n; });
    setLoadingKeys(prev => new Set([...prev, key]));
    const fetchers = fetcherMap(filters);
    const fn = (fetchers as any)[key];
    if (!fn) return;
    fn()
      .then((r: any) => setData((s: any) => ({ ...s, ...r })))
      .catch(() => setErrors(prev => ({ ...prev, [key]: true })))
      .finally(() => {
        setLoadingKeys(prev => { const n = new Set(prev); n.delete(key); return n; });
      });
  };

  const toggle = (key: string, value: string) => {
    setFilters(prev => {
      const cur = (prev as any)[key] as string[];
      return { ...prev, [key]: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value] };
    });
  };

  const clearAll = () => {
    const { date_from, date_to } = getDefaultDateRange(opts.min_month, opts.max_month);
    setFilters({ ...EMPTY_FILTERS, date_from, date_to });
  };

  const defaultRange = getDefaultDateRange(opts.min_month, opts.max_month);
  const hasActive =
    filters.managers.length || filters.client_partners.length || filters.assignment_names.length ||
    filters.service_lines.length || filters.legacy_location.length ||
    filters.date_from !== defaultRange.date_from || filters.date_to !== defaultRange.date_to;

  const canDrill = isSingleMonth(filters.date_from, filters.date_to);

  const items: ChartItem[] = useMemo(() => [
    {
      id: "charged",
      title: "Charged £ Monthly",
      description: "Total value charged out to clients in GBP, tracked month by month.",
      isLoading: loadingKeys.has("charged"),
      hasError: errors.charged,
      onRetry: () => retryChart("charged"),
      getContextData: () => contextFromRows(data.charged, "month_label", { charged: "Charged £" }, "gbp"),
      render: (h) => (
        <ChartContainer config={{}} className={`${h} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.charged ?? []} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month_label" tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
              <YAxis width={55} tickFormatter={v => formatYAxisLabel(v, "gbp")} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="charged" name="Charged £" fill={CORE_PALETTE.forestGreen} />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      ),
    },
    {
      id: "billedNet",
      title: "Billed Net £ Monthly",
      description: "Total net amount actually billed to clients in GBP, tracked month by month.",
      isLoading: loadingKeys.has("billedNet"),
      hasError: errors.billedNet,
      onRetry: () => retryChart("billedNet"),
      getContextData: () => contextFromRows(data.billedNet, "month_label", { billed_net: "Billed net £" }, "gbp"),
      render: (h) => (
        <ChartContainer config={{}} className={`${h} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.billedNet ?? []} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month_label" tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
              <YAxis width={55} tickFormatter={v => formatYAxisLabel(v, "gbp")} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="billed_net" name="Billed Net £" fill={CORE_PALETTE.teal} />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      ),
    },
    {
      id: "writeOff",
      title: "Write Off £ Monthly",
      description: "Total value written off rather than billed, in GBP, tracked month by month.",
      isLoading: loadingKeys.has("writeOff"),
      hasError: errors.writeOff,
      onRetry: () => retryChart("writeOff"),
      getContextData: () => contextFromRows(data.writeOff, "month_label", { write_off: "Write-off £" }, "gbp"),
      render: (h) => (
        <ChartContainer config={{}} className={`${h} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.writeOff ?? []} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month_label" tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
              <YAxis width={55} tickFormatter={v => formatYAxisLabel(v, "gbp")} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="write_off" name="Write Off £" fill={CORE_PALETTE.red} />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      ),
    },
    {
      id: "pct",
      title: "Recovery % Monthly",
      description: "Share of charged value that was actually billed to clients rather than written off, tracked month by month.",
      isLoading: loadingKeys.has("pct"),
      hasError: errors.pct,
      onRetry: () => retryChart("pct"),
      getContextData: () => contextFromRows(data.pct, "month_label", { recovery_pct: "Recovery %" }, "percent"),
      render: (h) => (
        <ChartContainer config={{}} className={`${h} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.pct ?? []} margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month_label" tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
              <YAxis width={45} domain={[0, 100]} tickFormatter={v => formatYAxisLabel(v, "percent")} tick={AXIS_TICK_STYLE} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line type="monotone" dataKey="recovery_pct" name="Recovery %" stroke={CORE_PALETTE.actionOrange} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartContainer>
      ),
    },
    {
      id: "pctBySL",
      title: "Recovery % by Service Line",
      description: "Monthly recovery rate trend, one line per service line.",
      isLoading: loadingKeys.has("pctBySL"),
      hasError: errors.pctBySL,
      onRetry: () => retryChart("pctBySL"),
      getContextData: () => contextFromGrouped(data.pctBySL, "month_label", "percent"),
      render: (h) => {
        const series: string[] = data.pctBySL?.series ?? [];
        const rows: any[] = data.pctBySL?.data ?? [];
        return (
          <ChartContainer config={{}} className={`${h} w-full`}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month_label" tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
                <YAxis width={45} domain={[0, 100]} tickFormatter={v => formatYAxisLabel(v, "percent")} tick={AXIS_TICK_STYLE} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Legend />
                {series.map((s, i) => (
                  <Line key={s} type="monotone" dataKey={s} name={s} stroke={palette[i % palette.length]} strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        );
      },
    },
    {
      id: "pctByLoc",
      title: "Recovery % by Location",
      description: "Monthly recovery rate trend, one line per office location.",
      isLoading: loadingKeys.has("pctByLoc"),
      hasError: errors.pctByLoc,
      onRetry: () => retryChart("pctByLoc"),
      getContextData: () => contextFromGrouped(data.pctByLoc, "month_label", "percent"),
      render: (h) => {
        const series: string[] = data.pctByLoc?.series ?? [];
        const rows: any[] = data.pctByLoc?.data ?? [];
        return (
          <ChartContainer config={{}} className={`${h} w-full`}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} margin={CHART_MARGIN}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month_label" tick={renderMonthYearTick} height={MONTH_YEAR_AXIS_HEIGHT} />
                <YAxis width={45} domain={[0, 100]} tickFormatter={v => formatYAxisLabel(v, "percent")} tick={AXIS_TICK_STYLE} />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Legend />
                {series.map((s, i) => (
                  <Line key={s} type="monotone" dataKey={s} name={s} stroke={palette[i % palette.length]} strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartContainer>
        );
      },
    },
    {
      id: "topMgrs",
      title: "Top Managers by Write-Off",
      description: "Managers with the highest write-off value in the selected period.",
      isLoading: loadingKeys.has("topMgrs"),
      hasError: errors.topMgrs,
      onRetry: () => retryChart("topMgrs"),
      getContextData: () => contextFromRows(data.topMgrs, "manager", { write_off: "Write-off £" }, "gbp"),
      render: (h) => (
        <ChartContainer config={{}} className={`${h} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.topMgrs ?? []} layout="vertical" margin={{ ...CHART_MARGIN, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={v => formatYAxisLabel(v, "gbp")} tick={AXIS_TICK_STYLE} />
              <YAxis type="category" dataKey="manager" tick={renderCategoryTick} width={130} interval={0} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="write_off" name="Write Off £" fill={CORE_PALETTE.red} />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      ),
    },
    {
      id: "topAssign",
      title: "Top Assignments by Billed Net",
      description: "Assignments generating the highest net billed value in the selected period.",
      isLoading: loadingKeys.has("topAssign"),
      hasError: errors.topAssign,
      onRetry: () => retryChart("topAssign"),
      getContextData: () => contextFromRows(data.topAssign, "assignment_name", { billed_net: "Billed net £" }, "gbp"),
      render: (h) => (
        <ChartContainer config={{}} className={`${h} w-full`}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data.topAssign ?? []} layout="vertical" margin={{ ...CHART_MARGIN, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={v => formatYAxisLabel(v, "gbp")} tick={AXIS_TICK_STYLE} />
              <YAxis type="category" dataKey="assignment_name" tick={renderCategoryTick} width={190} interval={0} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar dataKey="billed_net" name="Billed Net £" fill={CORE_PALETTE.teal} />
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      ),
    },
  ], [data, errors, loadingKeys, palette]); // eslint-disable-line react-hooks/exhaustive-deps

  const anyLoading = loadingKeys.size > 0;

  return (
    <SidebarProvider open={sidebarOpen} onOpenChange={handleSidebarChange}>
      <div className="min-h-dvh flex w-full bg-background">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0 h-dvh overflow-hidden">
          <MobileTopBar title="Recovery Rate" />
          <main className="flex-1 flex overflow-hidden min-h-0">
            <div className="flex-1 min-w-0 p-2 sm:p-3 space-y-4 sm:space-y-6 overflow-auto">
              {noAccess ? (
                <NoAccess />
              ) : initialLoading ? (
                <UtilisationSkeleton count={items.length} filterCount={5} />
              ) : (
                <>
                  <GenericFilterBar
                    fields={[
                      { key: "managers", label: "Manager", options: [...new Set([...opts.managers, ...filters.managers])], availableOptions: opts.managers, selected: filters.managers, searchable: true },
                      { key: "client_partners", label: "Client Partner", options: [...new Set([...opts.client_partners, ...filters.client_partners])], availableOptions: opts.client_partners, selected: filters.client_partners },
                      { key: "assignment_names", label: "Assignment", options: [...new Set([...opts.assignment_names, ...filters.assignment_names])], availableOptions: opts.assignment_names, selected: filters.assignment_names, searchable: true },
                      { key: "service_lines", label: "Service Line", options: [...new Set([...opts.service_lines, ...filters.service_lines])], availableOptions: opts.service_lines, selected: filters.service_lines },
                      { key: "legacy_location", label: "Location", options: [...new Set([...opts.legacy_location, ...filters.legacy_location])], availableOptions: opts.legacy_location, selected: filters.legacy_location },
                    ]}
                    onToggle={toggle}
                    onClearAll={clearAll}
                    min_month={opts.min_month}
                    max_month={opts.max_month}
                    date_from={filters.date_from}
                    date_to={filters.date_to}
                    onDateChange={(date_from, date_to) => setFilters(prev => ({ ...prev, date_from, date_to }))}
                    isLoading={anyLoading || optionsLoading}
                    hasActiveFilters={!!hasActive}
                  />
                  <div className="flex items-center justify-end gap-2">
                    {!canDrill && (
                      <span className="text-xs text-muted-foreground">
                        Set Start &amp; End to the same month to drill through
                      </span>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      disabled={!canDrill}
                      onClick={() =>
                        navigate("/metrics/recovery-rate/drill-through", {
                          state: {
                            baseFilters: {
                              managers: filters.managers,
                              client_partners: filters.client_partners,
                              assignment_names: filters.assignment_names,
                              service_lines: filters.service_lines,
                              legacy_location: filters.legacy_location,
                            },
                            month: filters.date_from,
                          },
                        })
                      }
                    >
                      <Table2 className="h-4 w-4" />
                      Drill Through
                    </Button>
                  </div>
                  <SimpleChartGrid items={items} />
                </>
              )}
            </div>
            <ChatDock
              open={isAssistantOpen}
              onOpenChange={setIsAssistantOpen}
              chartItems={items}
            />
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default RecoveryPage;

function readSavedRecoveryFilters(): RecoveryFilters | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(FILTERS_STORAGE_KEY) ?? "null");
    if (!parsed || typeof parsed !== "object") return null;
    const strings = (value: unknown) => Array.isArray(value) && value.every(item => typeof item === "string") ? value : [];
    return { managers: strings(parsed.managers), client_partners: strings(parsed.client_partners), assignment_names: strings(parsed.assignment_names), service_lines: strings(parsed.service_lines), legacy_location: strings(parsed.legacy_location), date_from: typeof parsed.date_from === "string" ? parsed.date_from : null, date_to: typeof parsed.date_to === "string" ? parsed.date_to : null };
  } catch { return null; }
}
